import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  keyframes,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

// 🎨 Animation keyframes
const slideIn = keyframes`
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const pulseIcon = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

export type AlertType = 'success' | 'error' | 'warning' | 'info';

interface ModernAlertProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type?: AlertType;
  confirmText?: string;
  showCancel?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const ModernAlert: React.FC<ModernAlertProps> = ({
  open,
  onClose,
  title,
  message,
  type = 'info',
  confirmText = 'OK',
  showCancel = false,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();

  const getTypeConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: <SuccessIcon sx={{ fontSize: 64, color: 'success.main' }} />,
          color: theme.palette.success.main,
          gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          bgColor: alpha(theme.palette.success.main, 0.1),
        };
      case 'error':
        return {
          icon: <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />,
          color: theme.palette.error.main,
          gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          bgColor: alpha(theme.palette.error.main, 0.1),
        };
      case 'warning':
        return {
          icon: <WarningIcon sx={{ fontSize: 64, color: 'warning.main' }} />,
          color: theme.palette.warning.main,
          gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          bgColor: alpha(theme.palette.warning.main, 0.1),
        };
      default:
        return {
          icon: <InfoIcon sx={{ fontSize: 64, color: 'info.main' }} />,
          color: theme.palette.info.main,
          gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          bgColor: alpha(theme.palette.info.main, 0.1),
        };
    }
  };

  const config = getTypeConfig();

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      onClose();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          animation: `${slideIn} 0.3s ease-out`,
          overflow: 'visible',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%)',
            backdropFilter: 'blur(8px)',
          },
        },
      }}
    >
      {/* Header with colored background */}
      <Box
        sx={{
          background: config.gradient,
          color: 'white',
          p: 3,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 50%)',
            zIndex: 1,
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          {/* Animated Icon */}
          <Box
            sx={{
              animation: `${pulseIcon} 2s ease-in-out infinite`,
              mb: 2,
            }}
          >
            {React.cloneElement(config.icon, { sx: { fontSize: 64, color: 'white' } })}
          </Box>
          
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700,
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            {title}
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 4, textAlign: 'center' }}>
        <Box
          sx={{
            animation: `${scaleIn} 0.4s ease-out 0.1s both`,
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.primary',
              fontSize: '1.1rem',
              lineHeight: 1.6,
              whiteSpace: 'pre-line', // Allow line breaks in message
            }}
          >
            {message}
          </Typography>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions 
        sx={{ 
          p: 3, 
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            animation: `${scaleIn} 0.4s ease-out 0.2s both`,
            display: 'flex',
            gap: 2,
          }}
        >
          {showCancel && (
            <Button
              onClick={handleCancel}
              variant="outlined"
              size="large"
              sx={{
                borderRadius: 3,
                borderWidth: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
              }}
            >
              Zrušit
            </Button>
          )}
          
          <Button
            onClick={handleConfirm}
            variant="contained"
            size="large"
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              background: config.gradient,
              '&:hover': {
                background: config.gradient,
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 25px ${alpha(config.color, 0.4)}`,
              },
            }}
          >
            {confirmText}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ModernAlert;

